import Phaser from 'phaser';

/**
 * StormSkyLayer
 *
 * A three-layer dark cloud overlay rendered above the painted background and
 * below the foreground / ambient sprites on the World 3 ship stages. The
 * layers drift horizontally at slightly different speeds for parallax depth,
 * and their alpha is tied to the player's stage clear progress so the sky
 * "clears" as bubbles are popped.
 *
 * Usage:
 *   this.stormSky = new StormSkyLayer(this);
 *   this.stormSky.create();
 *   ...
 *   this.stormSky.setProgress(0.0);   // full storm
 *   this.stormSky.setProgress(0.5);   // half cleared
 *   this.stormSky.setProgress(1.0);   // sunny
 *   ...
 *   this.stormSky.destroy();          // when leaving the scene
 *
 * Each call to setProgress() *tweens* the alpha smoothly over a short
 * duration so individual pops produce a satisfying micro-reveal rather
 * than a flickery snap.
 */

export interface StormSkyLayerOptions {
  /**
   * Asset keys for the three cloud strip textures, ordered back-to-front:
   *   [0] = wispiest / back   (lightest, highest in sky)
   *   [1] = mid-distance      (medium density)
   *   [2] = heaviest / front  (densest, sits low against the horizon)
   * Defaults match the assets generated by `scripts/generate-storm-sky-clouds.py`.
   */
  textureKeys?: [string, string, string];
}

interface LayerSpec {
  textureKey: string;
  /** Y position of the strip's TOP edge in 1920×1080 canvas space. */
  yOffset: number;
  /** Horizontal drift speed in pixels/sec. Negative drifts to the left. */
  driftPxPerSec: number;
  /** Render depth (lower = behind). All sit between bg (-100) and ambient (0). */
  depth: number;
  /** Per-layer alpha multiplier when storm is "full" (progress = 0). */
  baseAlpha: number;
  // ── Subtle live motion ─────────────────────────────────────────────
  /** Vertical bob amplitude in pixels (peak displacement from baseY). */
  bobAmpPx: number;
  /** Vertical bob period in ms (full sine cycle). */
  bobPeriodMs: number;
  /** Bob phase offset (radians) — randomized per layer at create time. */
  bobPhase: number;
  /** Alpha pulse amount as a fraction of baseAlpha (e.g. 0.06 = ±6%). */
  pulseAmount: number;
  /** Alpha pulse period in ms. */
  pulsePeriodMs: number;
  /** Alpha pulse phase offset (radians) — randomized per layer. */
  pulsePhase: number;
}

export class StormSkyLayer {
  private scene: Phaser.Scene;
  private opts: Required<StormSkyLayerOptions>;
  private layers: {
    sprite: Phaser.GameObjects.TileSprite;
    spec: LayerSpec;
    /** Resting Y position before bob — used as the bob center. */
    baseY: number;
  }[] = [];
  private progress = 0; // 0 = full storm, 1 = sunny
  private currentAlphaMultiplier = 1; // smooth-tweened version of (1 - progress)
  private alphaTween?: Phaser.Tweens.Tween;
  private updateBound?: (time: number, delta: number) => void;
  private destroyed = false;

  // Stage-space sizing (canvas is locked to 1920×1080 — see CLAUDE.md).
  // Each strip's height is now read from its texture at runtime (variable
  // per layer, see fit_to_strip in scripts/generate-storm-sky-clouds.py),
  // so STRIP_H is no longer a fixed constant.
  private static readonly CANVAS_W = 1920;
  private static readonly STRIP_W = 1920;

  constructor(scene: Phaser.Scene, options: StormSkyLayerOptions = {}) {
    this.scene = scene;
    this.opts = {
      textureKeys: options.textureKeys ?? [
        'storm_sky_layer_3', // back wispy
        'storm_sky_layer_2', // mid
        'storm_sky_layer_1', // foreground heaviest
      ],
    };
  }

  /**
   * Create the three TileSprite layers. Skips silently if any of the three
   * textures isn't loaded — the rest of the scene continues to work without
   * the storm sky overlay.
   */
  create(): void {
    const [backKey, midKey, frontKey] = this.opts.textureKeys;
    const allLoaded = [backKey, midKey, frontKey].every(k => this.scene.textures.exists(k));
    if (!allLoaded) {
      console.warn('⚠️ StormSkyLayer: one or more cloud textures missing, skipping overlay creation');
      return;
    }

    // Random phase per layer so the bob/pulse never line up in unison —
    // organic ambient motion, not a synchronized animation.
    const r = () => Math.random() * Math.PI * 2;

    const specs: LayerSpec[] = [
      // 3D-rendered storm cloud sprites — full opacity. The cloud band
      // anchors at the TOP of the canvas (the sky is up, not in the middle).
      // The front-heaviest layer sits at yOffset=0 so canvas y=0 is densely
      // covered. Mid + back stagger ABOVE (-20 / -40) for parallax depth —
      // they extend off-canvas at the top but their bodies still drape down
      // into the upper sky region.
      // Subtle bob (vertical sine, 2-3 px peak) + alpha pulse (±5-8%) per
      // layer with different periods so the storm feels ALIVE without ever
      // calling attention to itself.
      {
        textureKey: backKey,  yOffset: -40, driftPxPerSec: -3,  depth: -90, baseAlpha: 0.95,
        bobAmpPx: 2.0, bobPeriodMs: 14000, bobPhase: r(),
        pulseAmount: 0.05, pulsePeriodMs: 8000, pulsePhase: r(),
      },
      {
        textureKey: midKey,   yOffset: -20, driftPxPerSec: -6,  depth: -85, baseAlpha: 1.00,
        bobAmpPx: 2.5, bobPeriodMs: 11000, bobPhase: r(),
        pulseAmount: 0.06, pulsePeriodMs: 7000, pulsePhase: r(),
      },
      {
        textureKey: frontKey, yOffset:   0, driftPxPerSec: -10, depth: -80, baseAlpha: 1.00,
        bobAmpPx: 3.0, bobPeriodMs:  9000, bobPhase: r(),
        pulseAmount: 0.08, pulsePeriodMs: 6000, pulsePhase: r(),
      },
    ];

    for (const spec of specs) {
      // Each layer's asset has its own natural height (variable per layer
      // — see scripts/generate-storm-sky-clouds.py: width-fit, no crop).
      const tex = this.scene.textures.get(spec.textureKey);
      const naturalH = tex.source?.[0]?.height ?? 600;
      const baseY = spec.yOffset + naturalH / 2;

      const sprite = this.scene.add.tileSprite(
        StormSkyLayer.CANVAS_W / 2, // center x
        baseY,                      // center y → strip top sits at yOffset
        StormSkyLayer.STRIP_W,
        naturalH,
        spec.textureKey,
      );
      sprite.setOrigin(0.5);
      sprite.setDepth(spec.depth);
      sprite.setAlpha(spec.baseAlpha);
      // No tint — the 3D-rendered cloud sprite carries the right slate-blue
      // palette baked in. Bottom alpha gradient (baked into the asset by
      // generate-storm-sky-clouds.py) handles the soft sky blend.
      // No scrollFactor change — sky moves with camera (stays put on screen).
      this.layers.push({ sprite, spec, baseY });
    }

    // Hook update for drift
    this.updateBound = this.update.bind(this);
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.updateBound);

    console.log(`☁️  StormSkyLayer created (${this.layers.length} layers, full storm)`);
  }

  /**
   * Set the storm progress 0..1.
   *   0 = full storm (all clouds at base alpha)
   *   1 = clear sky (clouds at 0 alpha)
   * The new value is tweened smoothly so individual bubble pops cause a
   * gentle reveal rather than a hard step.
   */
  setProgress(progress: number, opts: { duration?: number } = {}): void {
    if (this.destroyed) return;
    progress = Phaser.Math.Clamp(progress, 0, 1);
    this.progress = progress;
    const targetMul = 1 - progress;
    const duration = opts.duration ?? 280; // ms — short enough to feel immediate, long enough to smooth out

    if (this.alphaTween) this.alphaTween.stop();
    // Per-frame `update()` reads currentAlphaMultiplier and applies the
    // combined alpha (with the breathing pulse), so the tween only needs
    // to drive the multiplier — no onUpdate callback necessary.
    this.alphaTween = this.scene.tweens.add({
      targets: this,
      currentAlphaMultiplier: targetMul,
      duration,
      ease: 'Sine.Out',
      onComplete: () => { this.alphaTween = undefined; },
    });
  }

  /** Snap alpha immediately without tween — useful at scene start. */
  setProgressImmediate(progress: number): void {
    if (this.destroyed) return;
    if (this.alphaTween) { this.alphaTween.stop(); this.alphaTween = undefined; }
    this.progress = Phaser.Math.Clamp(progress, 0, 1);
    this.currentAlphaMultiplier = 1 - this.progress;
    // Don't snap alpha here — update() will apply it on the next frame
    // with the breathing pulse already factored in. (At scene start the
    // first frame fires within ~16ms.)
  }

  private update(time: number, delta: number): void {
    if (this.destroyed) return;
    const dt = delta / 1000;
    const TWO_PI = Math.PI * 2;
    for (const { sprite, spec, baseY } of this.layers) {
      // Horizontal drift — texture tile scrolls leftward.
      sprite.tilePositionX -= spec.driftPxPerSec * dt;

      // Subtle vertical bob — the cloud band rises and falls slightly,
      // like it's floating on a slow updraft.
      const bob = spec.bobAmpPx * Math.sin((time / spec.bobPeriodMs) * TWO_PI + spec.bobPhase);
      sprite.y = baseY + bob;

      // Alpha breathing — clouds densify and thin gently. Layered ON TOP
      // of the progress-driven currentAlphaMultiplier (multiplicative) so
      // bubble-pop fading still works the same.
      const pulse = 1 + spec.pulseAmount * Math.sin((time / spec.pulsePeriodMs) * TWO_PI + spec.pulsePhase);
      const a = spec.baseAlpha * this.currentAlphaMultiplier * pulse;
      sprite.setAlpha(Phaser.Math.Clamp(a, 0, 1));
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.alphaTween) { this.alphaTween.stop(); this.alphaTween = undefined; }
    if (this.updateBound) {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.updateBound);
      this.updateBound = undefined;
    }
    for (const { sprite } of this.layers) sprite.destroy();
    this.layers = [];
    console.log('☁️  StormSkyLayer destroyed');
  }
}
